import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { getMyAccount } from '../actions/account';
import { getMyBoats } from '../actions/boats';
import { sendPoiRequest } from '../actions/pois';
import COLORS from '../constants/colors';
import { requireAuthentication } from './AuthModal';
import DateTimeText from './DateTimeText';
import FormInput from './FormInput';

const i18nPrefix = 'PoiRequest';
const today = moment().format('YYYY-MM-DD');
const maxDate = moment().add(1, 'year').format('YYYY-MM-DD');

const PoiRequest = ({
  poi,
  fromDate: initialFromDate,
  toDate: initialToDate,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(
    moment(today).add(1, 'day').format('YYYY-MM-DD'),
  );
  const [minToDate, setMinToDate] = useState(
    moment(today).add(1, 'day').format('YYYY-MM-DD'),
  );
  const [accountData, setAccountData] = useState(null);
  const [boats, setBoats] = useState([]);
  const [selectedBoatId, setSelectedBoatId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadAccountAndBoats = useCallback(() => {
    setLoading(true);
    getMyAccount(
      (account) => {
        setAccountData(account);
        getMyBoats(
          (resBoats) => {
            setBoats(resBoats);
            const selectedId =
              account?.selected_boat_id ||
              (resBoats.length > 0 ? resBoats[0].id : null);
            setSelectedBoatId(selectedId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          },
        );
      },
      () => {
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    const parsedFrom = initialFromDate ? moment(initialFromDate) : moment();
    const safeFrom = parsedFrom.isValid() ? parsedFrom : moment();
    const parsedTo = initialToDate
      ? moment(initialToDate)
      : safeFrom.clone().add(1, 'day');
    const safeTo = parsedTo.isValid()
      ? parsedTo
      : safeFrom.clone().add(1, 'day');

    setFromDate(safeFrom.format('YYYY-MM-DD'));
    setToDate(safeTo.format('YYYY-MM-DD'));
    setMinToDate(safeFrom.clone().add(1, 'day').format('YYYY-MM-DD'));
  }, [initialFromDate, initialToDate]);

  useEffect(() => {
    const token = localStorage.getItem('AuthToken');
    if (!token) {
      setLoading(false);
      return;
    }
    loadAccountAndBoats();
  }, [loadAccountAndBoats]);

  const buttonDisabled = false;

  const isSpontaneousRequest = useMemo(() => {
    if (!fromDate) return false;
    const fromDateTime = moment(fromDate).set({
      hour: 17,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    const diffInHours = fromDateTime.diff(moment(), 'hours');
    return diffInHours < 48;
  }, [fromDate]);

  const onChangeToDate = useCallback((event) => {
    setToDate(event.target.value);
  }, []);

  const onChangeFromDate = useCallback(
    (event) => {
      const nextFromDate = event.target.value;
      setFromDate(nextFromDate);
      const nextMinToDate = moment(nextFromDate)
        .add(1, 'day')
        .format('YYYY-MM-DD');
      setMinToDate(nextMinToDate);
      if (moment(toDate).isSameOrBefore(moment(nextFromDate))) {
        setToDate(nextMinToDate);
      }
    },
    [toDate],
  );

  const onSendPoiRequest = useCallback(() => {
    if (!accountData?.account_id) {
      requireAuthentication({ onAuthSuccess: loadAccountAndBoats });
      return;
    }
    if (buttonDisabled) return;
    setErrorMessage('');
    setSuccessMessage('');
    setSubmitting(true);
    sendPoiRequest({
      poiId: poi.id,
      fromDate,
      toDate,
      language: accountData?.communication_language || 'EN',
      boatId: selectedBoatId,
      accountId: accountData?.account_id,
      comment,
      callback: () => {
        setSubmitting(false);
        setSuccessMessage(t(`${i18nPrefix}.successMessage`));
      },
      errorCallback: (err) => {
        let errorMessage = t('errors.somethingWentWrong');
        if (err?.response?.data?.language_variable) {
          errorMessage = t(`errors.${err.response.data.language_variable}`);
        }
        setSubmitting(false);
        setErrorMessage(errorMessage);
      },
    });
  }, [
    accountData?.account_id,
    accountData?.communication_language,
    buttonDisabled,
    comment,
    fromDate,
    poi?.id,
    loadAccountAndBoats,
    selectedBoatId,
    t,
    toDate,
  ]);

  return (
    <Container style={{ maxWidth: 680, padding: '24px 20px' }}>
      <h3 style={{ color: COLORS.MAIN_BLUE, fontWeight: 700 }}>
        {t(`${i18nPrefix}.header`)}
      </h3>
      {errorMessage && (
        <Alert variant='danger' style={{ marginTop: 12 }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert variant='success' style={{ marginTop: 12 }}>
          {successMessage}
        </Alert>
      )}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
          <Spinner
            style={{ width: 20, height: 20 }}
            animation='border'
            variant='primary'
            role='status'
          />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <DateTimeText
                id='poi_request_from_date'
                label={t('global.from')}
                value={fromDate}
                min={today}
                max={maxDate}
                timelabel=''
                time=''
                onChange={onChangeFromDate}
              />
            </Col>
            <Col>
              <DateTimeText
                id='poi_request_to_date'
                label={t('global.to')}
                value={toDate}
                min={minToDate}
                max={maxDate}
                timelabel=''
                time=''
                onChange={onChangeToDate}
              />
            </Col>
          </Row>

          {isSpontaneousRequest && (
            <div
              style={{
                marginTop: 10,
                marginBottom: 16,
                padding: '8px 12px',
                borderRadius: 8,
                background: 'rgba(254, 135, 135, 0.15)',
                color: COLORS.MAIN_RED,
                fontWeight: 600,
                fontSize: 13,
              }}>
              {t(`${i18nPrefix}.spontaneousRequestWarning`)}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <div style={styles.sectionTitle}>{t(`${i18nPrefix}.details`)}</div>
            <FormInput
              id='poi_request_harbour_name'
              label={t(`${i18nPrefix}.harbourName`)}
              value={poi?.title || ''}
              disabled
              last
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={styles.sectionTitle}>{t(`${i18nPrefix}.boatInfo`)}</div>
            <FormInput
              id='poi_request_boat'
              label={t(`${i18nPrefix}.boatInfo`)}
              as='select'
              value={selectedBoatId ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedBoatId(value ? Number(value) : null);
              }}>
              {boats.length === 0 && (
                <option value=''>{t(`${i18nPrefix}.boatInfo`)}</option>
              )}
              {boats.map((boat) => (
                <option key={boat.id} value={boat.id}>
                  {boat.name || `${t(`${i18nPrefix}.boatInfo`)} #${boat.id}`}
                </option>
              ))}
            </FormInput>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={styles.sectionTitle}>
              {t(`${i18nPrefix}.commentForMarina`)}
            </div>
            <FormInput
              id='poi_request_comment'
              label={t(`${i18nPrefix}.comment`)}
              as='textarea'
              rows={4}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            <div
              style={{ marginTop: 8, color: COLORS.MAIN_GRAY, fontSize: 12 }}>
              {t(`${i18nPrefix}.additionalInfo`)}
            </div>
          </div>

          <Button
            type='button'
            onClick={onSendPoiRequest}
            disabled={false}
            style={{
              marginTop: 18,
              width: '100%',
              background: buttonDisabled
                ? COLORS.GRAY_OUTLINE
                : COLORS.SECONDARY_BLUE,
              borderColor: buttonDisabled
                ? COLORS.GRAY_OUTLINE
                : COLORS.SECONDARY_BLUE,
              fontWeight: 700,
            }}>
            {submitting ? t('global.loading') : t(`${i18nPrefix}.sendRequest`)}
          </Button>
        </>
      )}
    </Container>
  );
};

const styles = {
  sectionTitle: {
    color: COLORS.MAIN_BLUE,
    fontWeight: 700,
    marginBottom: 8,
  },
};

export default PoiRequest;
